// Chakra Imports
import {
  Button,
  Box,
  Flex,
  Text,
  Image,
  useColorModeValue,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  SimpleGrid,
  DrawerOverlay,
  Stack,
  useToast,
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';

import { HSeparator } from '../../../../components/separator/Separator';
import TextField from '../../../../components/fields/TextField';
import InputField from '../../../../components/fields/InputField';
import ConfiguratorLogo from '../../../../assets/img/layout/ConfiguratorLogo.png';
import { getAirlineById, updateAirline } from '../hooks';
import { LoadingSpinner } from '../../../../constants/helper';

export default function EditAirline(props) {
  const { setOpenEditAccess, openEditAccess, setFetchApi, airlineId} = props;
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  // Chakra Color Mode
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const bgSeparator = useColorModeValue('secondaryGray.400', '#323B5D');
  const drawerBg = useColorModeValue('white', 'navy.800');

  const configuratorShadow = useColorModeValue(
    '-20px 17px 40px 4px rgba(112, 144, 176, 0.18)',
    '-22px 32px 51px 4px #0B1437',
  );

    const [values, setValues] = useState({
      name: "",
      code: "",
      description: "",
    });

    useEffect(() => {
      const fetchAirline = async () => {
        setLoading(true);
        try {
            let res = await getAirlineById(airlineId);
            if (res.success) {
                let airline = res.data;
                setValues(airline);
            }
        } catch (error) {
            console.error("Error fetching airline:", error);
        } finally {
            setLoading(false); // Set loading to false after fetching is complete
        }
      };

      fetchAirline(); // Call the async function
    }, [airlineId]);

    const [errors, setErrors] = useState(values);
  
    const handleChange = (e) => {
      setValues({ ...values, [e.target.name]: e.target.value });
    }
    const validate = () => {
      let temp = {};
      temp.name = values.name ? "" : "Name is required.";
  
      setErrors({
        ...temp,
      });
  
      return Object.values(temp).every((x) => x === "");
    };

    const handleSubmit = async () => {
      if(validate()){
        setLoading(true);
        try {
          let res = await updateAirline(values);
          if (res.success) {
            toast({
              title: "Airline updated successfully.",
              status: "success", // could be success, error, or info
              duration: 5000, // time in ms before the toast disappears
              isClosable: true, // allows user to manually close the toast
              position: "top-right",
            });
            setOpenEditAccess(false);
            setFetchApi((prev) => !prev);
          }
          else {
            toast({
              title: "Error",
              description: `${res?.data}`,
              status: "error", // could be success, error, or info
              duration: 5000, // time in ms before the toast disappears
              isClosable: true, // allows user to manually close the toast
              position: "top-right",
            });
          }
        }
        catch (err) {
          toast({
            title: "Error",
            description: `${err}`,
            status: "error", // could be success, error, or info
            duration: 5000, // time in ms before the toast disappears
            isClosable: true, // allows user to manually close the toast
            position: "top-right",
          });
        }
        finally {
          setLoading(false);
        }
      }
    }

  return (
    <>
      <Drawer
        isOpen={openEditAccess}
        onClose={() => setOpenEditAccess(false)}
        placement={document.documentElement.dir === 'rtl' ? 'left' : 'right'}
        blockScrollOnMount={false}
      >
        <DrawerOverlay />
        <DrawerContent
          boxShadow={configuratorShadow}
          w={{ base: 'calc(100vw - 32px)', md: '400px' }}
          maxW={{ base: 'calc(100vw - 32px)', md: '400px' }}
          ms={{
            base: '0px',
            sm: '16px',
          }}
          me={{
            base: '16px',
          }}
          my={{
            sm: '16px',
          }}
          borderRadius="16px"
          bg={drawerBg}
        >
          {loading ? <LoadingSpinner /> : 
        <>
          <DrawerHeader
            w={{ base: '100%', md: '400px' }}
            pt="24px"
            pb="0px"
            px="24px"
          >
            <DrawerCloseButton color={textColor} />
            <Flex alignItems="center">
              <Flex
                h="48px"
                w="48px"
                me="20px"
                borderRadius="999px"
                bgGradient="linear(to-b, brand.400, brand.600)"
              >
                <Image src={ConfiguratorLogo} />
              </Flex>
              <Box>
                <Text color={textColor} fontSize="xl" fontWeight="700">
                  Edit Airline
                </Text>
                <Text
                  display={'flex'}
                  color="secondaryGray.600"
                  fontSize="md"
                  fontWeight="500"
                >
                  Update existing airline
                </Text>
              </Box>
            </Flex>
            <HSeparator my="30px" bg={bgSeparator} />
          </DrawerHeader>
          <DrawerBody
            pt="0px"
            pb="24px"
            w={{ base: '100%', md: '400px' }}
            maxW="unset"
          >
            <Flex direction="column" w="100%">
              <Stack direction="column" spacing="20px">
                <SimpleGrid rows={{ base: 1, md: 2 }} gap="20px">
                  <InputField
                    mb="0px"
                    id="name"
                    placeholder="eg. test45"
                    label="Name"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    error={errors.name}
                  />
                  <InputField
                    mb="0px"
                    id="code"
                    label="Code"
                    type="text"
                    name="code"
                    value={values.code}
                    onChange={handleChange}
                  />
                  <TextField
                    mb="0px"
                    id="description"
                    label="Note"
                    type="textarea"
                    value={values.description}
                    name="description"
                    onChange={handleChange}
                  />
                </SimpleGrid>
              </Stack>
              <HSeparator my="30px" bg={bgSeparator} />
              <Flex justify="space-between" mt="10px">
                <Button
                  variant="light"
                  fontSize="sm"
                  borderRadius="16px"
                  w={{ base: '100px', md: '118px' }}
                  h="46px"
                  ms="auto"
                  onClick={() => setOpenEditAccess(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="darkBrand"
                  fontSize="sm"
                  borderRadius="16px"
                  w={{ base: '100px', md: '118px' }}
                  h="46px"
                  ms="auto"
                  onClick={handleSubmit}
                >
                  Save
                </Button>
              </Flex>
            </Flex>
          </DrawerBody>
          </>
        }   
        </DrawerContent>
      </Drawer>
    </>
  );
}
